import { KEEP_PLACE } from "@/lib/search";
import { errorWords } from "@alm/domain";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";
import { saveFile } from "@/lib/files/download";
import { orpc } from "@/lib/orpc";
import { useCan, useCanChangeRow, useFeature } from "@/lib/policy";
import { RecordTabs } from "@/components/app/patterns/record-tabs";
import { useZone } from "@/lib/facility/zone";
import { EventActivitiesCard } from "./-components/event-activities-card";
import { EventAssignees } from "./-components/event-assignees";
import { EventCancelDialog } from "./-components/event-cancel-dialog";
import { EventDetailHeader } from "./-components/event-detail-header";
import { EventDialog } from "./-components/event-dialog";
import { EventLeadsCard } from "./-components/event-leads-card";
import { EventNotesCard } from "./-components/event-notes-card";
import { EventOutcomeCard } from "./-components/event-outcome-card";
import { EventOutcomeDialog } from "./-components/event-outcome-dialog";
import { EventPlanningCard } from "./-components/event-planning-card";
import { EventRoiCard } from "./-components/event-roi-card";
import { EventSeriesCard } from "./-components/event-series-card";
import { EventSourcesCard } from "./-components/event-sources-card";

const TABS = [
  { value: "overview", label: "Overview" },
  { value: "leads", label: "Leads" },
  { value: "sources", label: "Referral sources" },
  { value: "notes", label: "Notes" },
] as const;
type Tab = (typeof TABS)[number]["value"];

export const Route = createFileRoute("/_authenticated/_crm/crm/events/$eventId")({
  validateSearch: (search: Record<string, unknown>): { tab?: Tab } => ({
    tab: TABS.some((tab) => tab.value === search.tab) ? (search.tab as Tab) : undefined,
  }),
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(
      orpc.crm.events.get.queryOptions({ input: { eventId: params.eventId } }),
    ),
  component: EventDetailPage,
});

const fail = (error: Error) => toast.error(errorWords(error));

function EventDetailPage() {
  const { eventId } = Route.useParams();
  const { tab: linkedTab } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: event } = useSuspenseQuery(
    orpc.crm.events.get.queryOptions({ input: { eventId } }),
  );
  // An event for every building, and what is recorded on it, is the organization's to change.
  const canWrite = useCanChangeRow("crm:manage")(event.facilityId);
  const managesHere = useCan("crm:manage", event.facilityId);
  const canReadActivities = useCan("activity:read", event.facilityId);
  const activitiesOn = useFeature("activity-calendar", event.facilityId);
  const zone = useZone(event.facilityId);
  const tabNavigate = Route.useNavigate();
  const tab: Tab = linkedTab ?? "overview";
  const setTab = (next: Tab) =>
    void tabNavigate({
      search: (prev) => ({ ...prev, tab: next === "overview" ? undefined : next }),
      ...KEEP_PLACE,
    });
  const [editing, setEditing] = useState(false);
  const [copying, setCopying] = useState(false);
  const [recording, setRecording] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const refresh = () => queryClient.invalidateQueries({ queryKey: orpc.crm.events.key() });
  const reopen = useMutation(
    orpc.crm.events.reopen.mutationOptions({
      onSuccess: refresh,
      onError: fail,
    }),
  );
  const remove = useMutation(
    orpc.crm.events.remove.mutationOptions({
      onSuccess: async () => {
        toast.success("Event deleted");
        await refresh();
        await navigate({ to: "/crm/events" });
      },
      onError: fail,
    }),
  );
  const invite = useMutation(
    orpc.crm.events.invite.mutationOptions({
      onSuccess: saveFile,
      onError: fail,
    }),
  );

  const count = (section: Tab) =>
    section === "leads"
      ? event.linkedLeads
      : section === "sources"
        ? event.linkedSources
        : section === "notes"
          ? event.notesCount
          : 0;

  return (
    <div className="flex flex-col gap-5">
      <EventDetailHeader
        event={event}
        zone={zone}
        canWrite={canWrite}
        viewOnly={!canWrite && managesHere}
        onEdit={() => setEditing(true)}
        onCopy={() => setCopying(true)}
        onOutcome={() => setRecording(true)}
        onCancel={() => setCancelling(true)}
        onReopen={() => reopen.mutate({ eventId: event.id })}
        onDelete={() => setDeleting(true)}
        onInvite={() => invite.mutate({ eventId: event.id })}
        tabs={
          <RecordTabs
            label="Event sections"
            tabs={TABS.map((entry) => ({
              ...entry,
              count: count(entry.value),
            }))}
            value={tab}
            onChange={setTab}
          />
        }
      />
      {tab === "overview" ? (
        <div className="grid gap-5 lg:grid-cols-2">
          <EventPlanningCard event={event} canWrite={canWrite} />
          <div className="flex flex-col gap-5">
            <EventOutcomeCard
              event={event}
              canWrite={canWrite}
              onRecord={() => setRecording(true)}
            />
            <EventAssignees event={event} canWrite={canWrite} />
          </div>
          <div className="lg:col-span-2">
            <EventRoiCard roi={event.roi} />
          </div>
          {canReadActivities && activitiesOn ? (
            <div className="lg:col-span-2">
              <EventActivitiesCard eventId={event.id} />
            </div>
          ) : null}
          {event.seriesId ? (
            <div className="lg:col-span-2">
              <EventSeriesCard
                event={{ ...event, seriesId: event.seriesId }}
                zone={zone}
                canWrite={canWrite}
              />
            </div>
          ) : null}
        </div>
      ) : null}
      {tab === "leads" ? <EventLeadsCard event={event} canWrite={canWrite} /> : null}
      {tab === "sources" ? <EventSourcesCard event={event} canWrite={canWrite} /> : null}
      {tab === "notes" ? <EventNotesCard eventId={event.id} canWrite={canWrite} /> : null}

      {editing ? <EventDialog event={event} onClose={() => setEditing(false)} /> : null}
      {copying ? (
        <EventDialog
          event={null}
          copyOf={event}
          onClose={() => setCopying(false)}
          onSaved={(saved) =>
            void navigate({
              to: "/crm/events/$eventId",
              params: { eventId: saved.id },
            })
          }
        />
      ) : null}
      {recording ? <EventOutcomeDialog event={event} onClose={() => setRecording(false)} /> : null}
      {cancelling ? <EventCancelDialog event={event} onClose={() => setCancelling(false)} /> : null}
      {deleting ? (
        <RemoveConfirm
          title={`Delete ${event.title}?`}
          description={
            event.seriesId
              ? "Only this meeting, planned in error. Its notes and assignees go with it; the rest of the series stays."
              : "Only for one planned in error. Its notes and assignees go with it."
          }
          confirmLabel="Delete event"
          onConfirm={() => remove.mutate({ eventId: event.id })}
          onClose={() => setDeleting(false)}
        />
      ) : null}
    </div>
  );
}
